import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { Box, Grid, IconButton } from '@mui/material'
import classNames from 'classnames'
import { AddressUtils, PhoneUtils } from '@pbt/pbt-ui-components'

import type {
  Contact,
  ContactSearchResult,
} from '~/api/graphql/generated/types'
import Typography from '~/components/elements/Typography/Typography'
import { isManualContactId } from '~/utils/pmrUtils'

import { ClinicDisplayStatus } from './utils/clinicStatusUtils'

import styles from './ClinicBrief.module.scss'

export interface ClinicBriefProps {
  contact: Contact | Partial<Contact | ContactSearchResult>
  defaultExpanded?: boolean
  foldable?: boolean
}

export const ClinicBrief = ({
  contact,
  foldable = false,
  defaultExpanded = true,
}: ClinicBriefProps) => {
  const { t } = useTranslation('TimeTable')
  const [expanded, setExpanded] = useState(defaultExpanded)

  const displayStatus =
    'id' in contact && isManualContactId(contact.id)
      ? ClinicDisplayStatus.Unverified
      : ClinicDisplayStatus.Verified

  const getStatusLabel = (): string => {
    switch (displayStatus) {
      case ClinicDisplayStatus.Verified:
        return t('TimeTable:PMR_CONSENT.CLINIC_STATUS.VERIFIED')
      case ClinicDisplayStatus.Unverified:
      default:
        return t('TimeTable:PMR_CONSENT.CLINIC_STATUS.UNVERIFIED')
    }
  }
  const formattedAddress = AddressUtils.formatAddress({
    address: contact.address ?? undefined,
    suite: contact.suite ?? undefined,
    city: contact.city ?? undefined,
    state: contact.state ?? undefined,
    zip: contact.zip ?? undefined,
  })
  const phoneFormatted = contact.phone
    ? PhoneUtils.formatPhoneNumber(PhoneUtils.parsePhoneNumber(contact.phone))
    : null

  const handleToggle = () => {
    setExpanded(!expanded)
  }

  return (
    <Grid container flexDirection="column">
      <Grid container item height="fit-content">
        {foldable && (
          <Grid item>
            <IconButton
              size="large"
              sx={{
                padding: '0 12px 2px 0',
                ...(foldable ? { cursor: 'pointer' } : {}),
              }}
              onClick={(e) => {
                e.stopPropagation()
                handleToggle()
              }}
            >
              <ExpandMore
                className={classNames(styles.chevron, {
                  [styles.chevronExpanded]: !expanded,
                })}
              />
            </IconButton>
          </Grid>
        )}
        <Grid container item xs alignItems="center" gap={1} wrap="nowrap">
          <Grid item>
            <Typography.H3>{contact.name}</Typography.H3>
          </Grid>
          <Grid item display="flex">
            <Box
              className={classNames(styles.badge, {
                [styles.badgeVerified]:
                  displayStatus === ClinicDisplayStatus.Verified,
                [styles.badgeUnverified]:
                  displayStatus === ClinicDisplayStatus.Unverified,
              })}
            >
              <Typography.Label
                className={classNames(styles.label, {
                  [styles.labelVerified]:
                    displayStatus === ClinicDisplayStatus.Verified,
                  [styles.labelUnverified]:
                    displayStatus === ClinicDisplayStatus.Unverified,
                })}
              >
                {getStatusLabel()}
              </Typography.Label>
            </Box>
          </Grid>
        </Grid>
      </Grid>

      {(!foldable || expanded) && (
        <Grid
          container
          item
          direction="column"
          ml={expanded && foldable ? 4 : undefined}
          spacing={0.5}
          width="auto"
        >
          {formattedAddress && (
            <Grid item>
              <Typography.Paragraph>{formattedAddress}</Typography.Paragraph>
            </Grid>
          )}

          {phoneFormatted && (
            <Grid item>
              <Typography.Paragraph>{phoneFormatted}</Typography.Paragraph>
            </Grid>
          )}
        </Grid>
      )}
    </Grid>
  )
}

export default ClinicBrief
